import { Download01, Plus, XClose } from '@untitled-ui/icons-react/build/cjs';
import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import pdf from 'src/assets/img/pdf-icon.svg';
import ButtonCmp from 'src/components/ButtonCmp';
import TooltipCmp from 'src/components/TooltipCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import { addUploadedDocument, removeUploadedDocument, selectFormData } from 'src/redux/bookingStepper.slice';
import { uploadCustomsDocuments } from 'src/services/QuoteService';
import WalToast from 'src/utils/WalToast';

interface IUploadedDocument {
    id: string;
    serverId?: number;
    fileName: string;
    fileSize: number;
    fileUrl?: string;
    previewUrl?: string;
    isUploading: boolean;
    isDeleting: boolean;
    documentType?: string;
    title?: string;
    uploadedBy?: string;
    uploadedByType?: string;
    createdAt?: string;
}

const MAX_DOCUMENTS = 4;

// Validate file integrity by checking magic bytes (file signature)
const validateFileIntegrity = async (file: File): Promise<boolean> => {
    try {
        const ext = file.name.split('.').pop()?.toLowerCase() || '';

        if (file.size === 0) return false;

        if (ext === 'svg') {
            const text = await file.slice(0, 100).text();
            const trimmed = text.trimStart();

            return trimmed.startsWith('<?xml') || trimmed.startsWith('<svg');
        }

        const buffer = await file.slice(0, 8).arrayBuffer();
        const bytes = new Uint8Array(buffer);

        switch (ext) {
            case 'pdf':
                // %PDF = 0x25 0x50 0x44 0x46
                return bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46;
            case 'png':
                // PNG signature: 0x89 0x50 0x4E 0x47
                return bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47;
            case 'jpg':
            case 'jpeg':
                // JPEG starts with 0xFF 0xD8 0xFF
                return bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff;
            default:
                return false;
        }
    } catch {
        return false;
    }
};

const UploadDocuments: React.FC<any> = ({ stepperFooterRef }) => {
    const { t } = useTranslation();
    const bookingFormData = useSelector(selectFormData);
    const dispatch = useDispatch();
    const fileNameRef = useRef<HTMLParagraphElement>(null);

    const uploadedDocuments: IUploadedDocument[] = bookingFormData?.uploadedDocuments || [];

    // Upload file to server
    const uploadFile = async (file: File, fileId: string): Promise<void> => {
        const formData = new FormData();
        formData.append('document', file);
        formData.append('quoteId', bookingFormData?.quoteId || '');

        try {
            const response = await uploadCustomsDocuments(formData);

            if (response?.data?.document) {
                const uploadedDoc = response.data.document;

                // Remove the temporary placeholder entry
                dispatch(removeUploadedDocument({ id: fileId }));

                const responseId = uploadedDoc.id?.toString();
                const documentId = responseId || `uploaded-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

                // Add the real server document
                dispatch(
                    addUploadedDocument({
                        id: documentId,
                        serverId: uploadedDoc.id,
                        fileName: uploadedDoc.fileName,
                        fileSize: uploadedDoc.fileSize,
                        fileUrl: uploadedDoc.fileUrl,
                        isUploading: false,
                        isDeleting: false,
                        documentType: uploadedDoc.documentType,
                        title: uploadedDoc.title,
                        uploadedBy: uploadedDoc.uploadedBy,
                        uploadedByType: uploadedDoc.uploadedByType,
                        createdAt: uploadedDoc.createdAt,
                    })
                );
                WalToast.success(`${file.name} uploaded successfully`);
            } else {
                dispatch(removeUploadedDocument({ id: fileId }));
            }
        } catch (error: any) {
            console.error('File upload error:', error);
            dispatch(removeUploadedDocument({ id: fileId }));

            const errorMessage = 'Document upload failed';
            WalToast.error(errorMessage);
        }
    };

    // Handle file upload
    const handleFileUpload = useCallback(
        async (event: React.ChangeEvent<HTMLInputElement>) => {
            const files = event.target.files;
            if (!files || files.length === 0) return;

            if (uploadedDocuments.length >= MAX_DOCUMENTS) {
                WalToast.error(t('BookOrder.Booking_Stepper.Custom_Documents.Max_File_Limit_Reached_Description'));
                event.target.value = '';

                return;
            }

            const allowedTypes = ['application/pdf', 'image/svg+xml', 'image/png', 'image/jpg', 'image/jpeg'];
            const maxSize = 10 * 1024 * 1024; // 10MB
            const remainingSlots = MAX_DOCUMENTS - uploadedDocuments.length;
            const filesToProcess = Array.from(files).slice(0, remainingSlots);

            if (Array.from(files).length > remainingSlots) {
                WalToast.error(t('BookOrder.Booking_Stepper.Custom_Documents.Max_File_Limit_Reached_Description'));
            }

            for (const file of filesToProcess) {
                if (!allowedTypes.includes(file.type)) {
                    WalToast.error(t('Common.File_must_be_image_and_document'));
                    continue;
                }

                if (file.size > maxSize) {
                    WalToast.error(t('Orders.Order_Stepper.File_Size_Max_10MB'));
                    continue;
                }

                const isValidFile = await validateFileIntegrity(file);

                if (!isValidFile) {
                    WalToast.error(`${file.name} ${t('ValidationMessages.File_appears_to_be_corrupted')}`);
                    continue;
                }

                const fileId = `${file.name}-${Date.now()}-${Math.random()}`;
                const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
                const isImage = ['png', 'jpg', 'jpeg'].includes(fileExtension);
                const previewUrl = isImage ? URL.createObjectURL(file) : undefined;

                // addUploadedDocument pushes to state via Immer — no stale closure issue
                // so all files in the loop accumulate correctly one by one
                dispatch(
                    addUploadedDocument({
                        id: fileId,
                        fileName: file.name,
                        fileSize: file.size,
                        previewUrl,
                        isUploading: true,
                        isDeleting: false,
                    })
                );

                uploadFile(file, fileId);
            }

            event.target.value = '';
        },
        [t, uploadedDocuments, bookingFormData?.quoteId, dispatch]
    );

    // Handle drag and drop
    const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
    }, []);

    const handleDragEnter = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
    }, []);

    const handleDrop = useCallback(
        (event: React.DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            event.stopPropagation();

            const files = event.dataTransfer.files;

            if (files && files.length > 0) {
                const syntheticEvent = {
                    target: { files },
                } as React.ChangeEvent<HTMLInputElement>;
                handleFileUpload(syntheticEvent);
            }
        },
        [handleFileUpload]
    );

    // Handle file removal
    const handleRemoveFile = useCallback(
        (fileId: string) => {
            const isAnyDeleting = uploadedDocuments.some((doc) => doc.isDeleting);
            if (isAnyDeleting) return;

            const documentToDelete = uploadedDocuments.find((doc) => doc.id === fileId);
            if (!documentToDelete) return;

            // Mark as deleting (addUploadedDocument updates in-place when id matches)
            dispatch(addUploadedDocument({ ...documentToDelete, isDeleting: true }));

            setTimeout(() => {
                dispatch(removeUploadedDocument({ id: fileId }));
                WalToast.success(`${documentToDelete.fileName} deleted successfully`);
            }, 500);
        },
        [uploadedDocuments, dispatch]
    );

    // Handle file download
    const handleDownloadFile = useCallback((document: IUploadedDocument) => {
        if (document.fileUrl) {
            const link = window.document.createElement('a');
            link.href = document.fileUrl;
            link.download = document.fileName;
            link.target = '_blank';
            window.document.body.appendChild(link);
            link.click();
            window.document.body.removeChild(link);
        } else if (document.previewUrl) {
            window.open(document.previewUrl, '_blank');
        }
    }, []);

    // Handle image error
    const onError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
        e.currentTarget.style.display = 'none';
    }, []);

    const handleNext = useCallback(async () => {
        // No validation needed for optional document upload
        // Just ensure data is saved
    }, []);

    const handleBack = useCallback(() => {
        // Data is already saved in Redux
    }, []);

    // Register handlers with parent via the ref
    useEffect(() => {
        const stepperHandlers = {
            triggerNext: handleNext,
            triggerBack: handleBack,
        };

        if (stepperFooterRef && typeof stepperFooterRef === 'object' && 'current' in stepperFooterRef) {
            stepperFooterRef.current = stepperHandlers;
        }
    }, [handleNext, handleBack, stepperFooterRef]);

    const isAtMaxLimit = uploadedDocuments.length >= MAX_DOCUMENTS;

    return (
        <div className="space-y-5">
            <div className="animate-field-item min-w-0">
                <div className="stepper-content-container border-b border-utilityGray200 pb-5">
                    <div className="stepper-lable-wrap flex">
                        <label className="stepper-label-common">{t('BookOrder.Booking_Stepper.Custom_Documents.Upload_Customs_Docs')}</label>
                    </div>
                    <div className="space-y-3 w-full min-w-0">
                        <div
                            className={`upload-file-box relative ${isAtMaxLimit ? 'opacity-50 cursor-not-allowed' : ''}`}
                            onDrop={!isAtMaxLimit ? handleDrop : undefined}
                            onDragOver={!isAtMaxLimit ? handleDragOver : undefined}
                            onDragEnter={!isAtMaxLimit ? handleDragEnter : undefined}
                        >
                            <div className="relative overflow-hidden w-full" style={{ cursor: isAtMaxLimit ? 'not-allowed' : 'pointer' }}>
                                <label htmlFor="fileSelect" style={{ cursor: isAtMaxLimit ? 'not-allowed' : 'pointer' }} className="text-center">
                                    <div className="mx-auto mb-1 flex items-center justify-center">
                                        <ButtonCmp
                                            type="button"
                                            className="btn_secondary_black"
                                            icon={<Plus className="w-4 h-4" />}
                                            disabled={isAtMaxLimit}
                                            onClick={() => (document.getElementById('fileSelect') as HTMLInputElement)?.click()}
                                        >
                                            Add File
                                        </ButtonCmp>
                                    </div>

                                    <p className="upload-file-box-text">{t('Common.File_type_With_PDF_desc')}</p>

                                    <input
                                        type="file"
                                        id="fileSelect"
                                        title=""
                                        className="w-full h-full opacity-0 absolute left-0 top-0 pointer-events-none"
                                        style={{ cursor: isAtMaxLimit ? 'not-allowed' : 'pointer' }}
                                        accept=".pdf,.svg,.png,.jpg,.jpeg"
                                        onChange={handleFileUpload}
                                        multiple
                                        disabled={isAtMaxLimit}
                                    />
                                </label>
                            </div>
                        </div>

                        {uploadedDocuments.length > 0 && (
                            <div>
                                <div className="flex -m-1 flex-wrap">
                                    {uploadedDocuments.map((document) => {
                                        const fileName = document.fileName || 'Unknown file';
                                        const fileSize = document.fileSize || 0;
                                        const isUploading = document.isUploading || false;
                                        const isDeletingCurrentFile = document.isDeleting || false;
                                        const isAnyDeleting = uploadedDocuments.some((doc) => doc.isDeleting);

                                        // Determine file extension and if it's an image
                                        const fileExtension = fileName.split('.').pop()?.toLowerCase() || '';
                                        const isImageExtension = ['png', 'jpg', 'jpeg', 'svg'].includes(fileExtension);
                                        const shouldShowImagePreview = isImageExtension;
                                        // Use fileUrl from server if available, otherwise use local preview
                                        const previewImageSrc = document.fileUrl || document.previewUrl || '';

                                        return (
                                            <div key={document.id} className="p-1 w-1/2">
                                                <div className="relative rounded-lg border border-utilityGray200 bg-white p-3 flex items-center gap-3 cursor-pointer">
                                                    {shouldShowImagePreview ? (
                                                        <img
                                                            src={previewImageSrc || ''}
                                                            className="w-8 h-8 rounded-full border border-utilityBlack border-opacity-[0.08] flex-none object-cover"
                                                            onError={onError}
                                                            alt={fileName}
                                                        />
                                                    ) : (
                                                        <div className="h-[34px] flex-none w-[34px]">
                                                            <img src={pdf} alt="PDF file" className="w-full h-full object-contain" />
                                                        </div>
                                                    )}
                                                    <div className="flex-1 overflow-hidden">
                                                        <TruncateTextCmp refName={fileNameRef} tooltipType="action" message={fileName}>
                                                            <p className="text-sm font-medium truncate text-primary700" ref={fileNameRef}>
                                                                {fileName}
                                                            </p>
                                                        </TruncateTextCmp>

                                                        <p className="text-[11px] font-normal leading-[18px] text-grayLight600">{(fileSize / 1024).toFixed(0)} KB</p>
                                                    </div>

                                                    <TooltipCmp message={t('Common.Download')} position="top">
                                                        <Download01 className="w-4 h-4 text-grayLight600 cursor-pointer" onClick={() => handleDownloadFile(document)} />
                                                    </TooltipCmp>

                                                    <div className="absolute -top-1.5 -right-1.5">
                                                        {isUploading || isDeletingCurrentFile ? (
                                                            <span className="loading loading-spinner text-primary w-4 h-4"></span>
                                                        ) : (
                                                            <div className="border border-utilityGray200 rounded-full w-4 h-4 bg-white">
                                                                <XClose
                                                                    className={`w-3.5 h-3.5 transition-colors ${
                                                                        isAnyDeleting ? 'text-grayLight400 cursor-not-allowed' : 'text-grayLight600 cursor-pointer hover:text-red-600'
                                                                    }`}
                                                                    onClick={() => !isAnyDeleting && handleRemoveFile(document.id)}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UploadDocuments;
